import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./DetailedTicket.module.css";
import { updateTicketStatus } from "../../../api/api";

const DetailedTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ticket, customerName, customerEmail, customerPhone } =
    location.state || {};

  const [ticketStatus, setTicketStatus] = React.useState(
    (ticket?.status || "open").toLowerCase()
  );

  const isResolved = ticketStatus === "resolved";
  const isClosed = ticketStatus === "closed";

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/login", {
        state: { error: "Session expired. Please log in again." },
      });
      return;
    }
    if (!ticket) {
      navigate("/dashboard/manage-customer");
      return;
    }
  }, [navigate, ticket]);

  if (!ticket) return null;

  const handleTakeActionClick = () => {
    navigate("/dashboard/ticket-response", {
      state: { ticket, customerName, customerEmail, customerPhone },
    });
  };

  const handleBackClick = () => navigate(-1);

  const patchStatus = async (nextStatus) => {
    await updateTicketStatus(ticket.ticket_id, nextStatus);
    setTicketStatus(nextStatus);
  };

  const prettyStatus = (s = "") => {
    const m = {
      open: "Open",
      in_progress: "In Progress",
      resolved: "Resolved",
      closed: "Closed",
    };
    return m[s.toLowerCase()] ?? s;
  };

  return (
    <div className={styles.detailedTicket}>
      <div className={styles.headerRow}>
        <h2>Detailed Ticket</h2>
        <span className={`${styles.status} ${styles[ticketStatus]}`}>
          {prettyStatus(ticketStatus)}
        </span>
      </div>

      <table className={styles.detailsTable}>
        <tbody>
          <tr>
            <td>Customer Name</td>
            <td>{customerName}</td>
          </tr>
          <tr>
            <td>Email ID</td>
            <td>{customerEmail}</td>
          </tr>
          <tr>
            <td>Phone Number</td>
            <td>{customerPhone}</td>
          </tr>
          <tr>
            <td>Subject</td>
            <td>{ticket.subject}</td>
          </tr>
          <tr>
            <td>Issue Type</td>
            <td>{ticket.issue_type || "N/A"}</td>
          </tr>
          <tr>
            <td>Priority Level</td>
            <td>{ticket.priority_level}</td>
          </tr>
          <tr>
            <td>Description</td>
            <td>{ticket.description}</td>
          </tr>
          {ticket.attachment_url && (
            <tr>
              <td>Attachment</td>
              <td>
                <a
                  href={ticket.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Attachment
                </a>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {(isResolved || isClosed) && (
        <div className={styles.notice}>
          {isClosed
            ? "This ticket is closed. Further actions are disabled."
            : "This ticket is resolved. You may close it or reopen if needed."}
        </div>
      )}

      <div className={styles.actionButtons}>
        <button
          className={styles.takeActionButton}
          onClick={handleTakeActionClick}
          disabled={isResolved || isClosed}
          title={
            isResolved || isClosed
              ? "Actions disabled for resolved/closed tickets"
              : "Respond to ticket"
          }
        >
          Take Action
        </button>

        {["open", "in_progress"].includes(ticketStatus) && (
          <button
            className={styles.resolveButton}
            onClick={async () => {
              try {
                await patchStatus("resolved");
              } catch (e) {
                console.error("Failed to mark resolved", e);
              }
            }}
          >
            Mark as Resolved
          </button>
        )}

        {ticketStatus === "resolved" && (
          <button
            className={styles.closeButton}
            onClick={async () => {
              try {
                await patchStatus("closed");
              } catch (e) {
                console.error("Failed to close ticket", e);
              }
            }}
          >
            Close Ticket
          </button>
        )}

        <button className={styles.backButton} onClick={handleBackClick}>
          Back
        </button>
      </div>
    </div>
  );
};

export default DetailedTicket;
